import { defineStore } from "pinia";
import { useOrgSettings } from "@/services";
import { defaultAddressSearchProvider, formatAddress } from "@/util/helpers";

import mapboxgl from "mapbox-gl";
import { PbAddressResponse, SearchProviderClient } from "@/types";
import { useAxios } from "@/config";

export interface AddressSearchProvider {
  access_token: string;
  clientID: string;
  expiresIn: number;
  issuedAt: number;
  org: string;
  tokenType: string;
}

interface PreciselyState {
  provider: null | AddressSearchProvider;
  searchText: string;
}
export const usePrecisely = defineStore("precisely", {
  state: (): PreciselyState => ({
    provider: null,
    searchText: "",
  }),
  getters: {
    client(): SearchProviderClient {
      return useOrgSettings().searchProvider?.providerType === "PitneyBowes"
        ? useOrgSettings().searchProvider
        : defaultAddressSearchProvider;
    },
  },
  actions: {
    async getToken(refresh = false): Promise<AddressSearchProvider | null> {
      return this.client && this.provider && !refresh
        ? Promise.resolve(this.provider)
        : useAxios()
            .$http.get(`${this.client.api.gamma}`, {
              headers: {
                "x-gammaws-key": this.client.licenceKey,
                "Content-Type": "application/json",
              },
            })
            .then((r) => {
              this.provider = {
                ...r.data,
                expiresIn: Number(r.data.expiresIn),
                issuedAt: Number(r.data.issuedAt),
              };
              return this.provider;
            })
            .catch(() => this.getToken(true));
    },
    async searchAddress(searchText: string) {
      return this.getToken().then(() =>
        useAxios().$http.get(
          this.client.api.base + this.client.api.autocomplete,
          {
            params: {
              searchText,
              country: this.client.country,
              maxCandidates: this.client.maxCandidates,
            },
            headers: { Authorization: `Bearer ${this.provider?.access_token}` },
          }
        )
      );
    },
    async forwardGeocode(mainAddress: string) {
      return this.getToken().then(() =>
        useAxios().$http.get(
          `${this.client.api.base}${this.client.api.geocode}`,
          {
            params: {
              mainAddress,
              country: this.client.country,
              fallbackGeo: false,
              fallbackPostal: true,
              maxCands: this.client.maxCandidates,
            },
            headers: { Authorization: `Bearer ${this.provider?.access_token}` },
          }
        )
      );
    },
    async reverseGeocode(center: mapboxgl.LngLat) {
      return this.getToken()
        .then(() =>
          useAxios().$http.get(
            `${this.client.api.base}${this.client.api.reverseGeocode}`,
            {
              params: {
                x: center.lng,
                y: center.lat,
                country: this.client.country,
                distance: this.client.distance,
              },
              headers: {
                Authorization: `Bearer ${this.provider?.access_token}`,
              },
            }
          )
        )
        .then((r) => {
          const items: string[] = (r.data.candidates || []).map(
            (c: PbAddressResponse) => formatAddress(c.address)
          );
          if (items.length === 0) {
            // todo: display nothing found in UI
            console.log("nothing found", r.data);
          } else {
            this.searchText = items[0];
            if (items.length > 1) {
              console.log("multiple candidates", items);
              /** todo: handle multiple candidates
               *  1. move searchResults in state (from PreciselySearch.vue)
               *  2. populate searchResults with candidates so user could click on desired one
               **/
            }
          }
        });
    },
  },
});
